/**
 * Radial Progress Component
 *
 * Displays a radial gauge for rate/percentage metrics with color-coded status
 * and accessible status indicators (icons) beyond color alone.
 */

import { RadialBar, RadialBarChart, PolarAngleAxis } from 'recharts';
import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';

interface RadialProgressProps {
  title: string;
  value: number | undefined;
  maxValue?: number;
  isLoading: boolean;
  format?: 'percent' | 'decimal' | 'number';
  thresholds?: {
    warning: number;
    critical: number;
    inverse?: boolean; // If true, lower values are better
  };
  /** Optional tooltip explaining what this metric means */
  tooltip?: string;
}

type StatusLevel = 'success' | 'warning' | 'critical';

function getStatusLevel(
  value: number,
  thresholds?: RadialProgressProps['thresholds'],
): StatusLevel {
  if (!thresholds) return 'success';

  const { warning, critical, inverse = false } = thresholds;

  if (inverse) {
    // Lower is better (e.g., error rate)
    if (value >= critical) return 'critical';
    if (value >= warning) return 'warning';
    return 'success';
  } else {
    // Higher is better (e.g., success rate, filter effectiveness)
    if (value <= critical) return 'critical';
    if (value <= warning) return 'warning';
    return 'success';
  }
}

function getStatusColor(status: StatusLevel): string {
  switch (status) {
    case 'critical':
      return 'hsl(var(--destructive))';
    case 'warning':
      return 'var(--chart-4)';
    case 'success':
    default:
      return 'var(--chart-2)';
  }
}

function getStatusIcon(status: StatusLevel) {
  switch (status) {
    case 'critical':
      return <XCircle className="h-4 w-4 text-destructive" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'success':
    default:
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }
}

function getStatusLabel(status: StatusLevel): string {
  switch (status) {
    case 'critical':
      return 'Critical';
    case 'warning':
      return 'Warning';
    case 'success':
    default:
      return 'Good';
  }
}

function formatValue(
  value: number,
  format: RadialProgressProps['format'],
): string {
  switch (format) {
    case 'percent':
      return `${Math.round(value * 100)}%`;
    case 'decimal':
      return value.toFixed(2);
    case 'number':
    default:
      return Math.round(value).toString();
  }
}

const chartConfig = {
  value: {
    label: 'Value',
  },
} satisfies ChartConfig;

export function RadialProgress({
  title,
  value,
  maxValue = 1,
  isLoading,
  format = 'percent',
  thresholds,
  tooltip,
}: RadialProgressProps) {
  const displayValue = value ?? 0;
  const normalizedValue = Math.min((displayValue / maxValue) * 100, 100);
  const statusLevel = getStatusLevel(displayValue, thresholds);
  const color = getStatusColor(statusLevel);

  const chartData = [
    {
      name: title,
      value: normalizedValue,
      fill: color,
    },
  ];

  // Show skeleton when loading OR when data hasn't arrived yet (value is undefined)
  const showSkeleton = isLoading || value === undefined;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex cursor-help"
                    aria-label="More information"
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {/* Status indicator with icon for accessibility (not just color) */}
          {!showSkeleton && thresholds && (
            <div
              className="flex items-center gap-1"
              role="status"
              aria-label={`Status: ${getStatusLabel(statusLevel)}`}
            >
              {getStatusIcon(statusLevel)}
              <span className="sr-only">{getStatusLabel(statusLevel)}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center pb-4">
        {showSkeleton ? (
          <Skeleton className="h-[120px] w-[120px] rounded-full" />
        ) : (
          <ChartContainer
            config={chartConfig}
            className="h-[120px] w-[120px]"
            aria-label={`${title}: ${formatValue(displayValue, format)}, Status: ${getStatusLabel(statusLevel)}`}
          >
            <RadialBarChart
              data={chartData}
              startAngle={90}
              endAngle={-270}
              innerRadius="70%"
              outerRadius="100%"
              barSize={12}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                background={{ fill: 'hsl(var(--muted))' }}
                dataKey="value"
                cornerRadius={6}
                fill={color}
              />
              {/* Center text */}
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-foreground"
              >
                <tspan x="50%" dy="-0.1em" className="text-xl font-bold">
                  {formatValue(displayValue, format)}
                </tspan>
              </text>
            </RadialBarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
